import type { OrderRequestedShipping } from '@skupreme/api-contracts/core';

/** What the fulfillment UI shows about the method the marketplace asked for. */
export type RequestedShippingDescription = {
  /** The requested method, or null when the marketplace named none. */
  value: string | null;
  /** The marketplace's own wording for it, when it adds something to `value`. */
  detail: string | null;
  /** A carrier the marketplace pinned, which the server already enforces. */
  pinned: boolean;
};

const joinCarrierService = (requested: OrderRequestedShipping) =>
  [requested.carrier, requested.service].filter(Boolean).join(' ') || null;

/**
 * An order with no marketplace instruction has to read as "not provided" rather
 * than as Standard: the operator is the one who decides in that case, and until
 * this shipped the UI told them Standard was requested when nothing was.
 */
export function describeRequestedShipping(
  requested: OrderRequestedShipping | null | undefined,
): RequestedShippingDescription {
  if (!requested) return { value: null, detail: null, pinned: false };

  const carrierService = joinCarrierService(requested);
  const value = requested.speed || carrierService;
  const raw = requested.raw && requested.raw !== value ? requested.raw : null;

  return {
    value,
    detail: raw ?? (requested.speed ? carrierService : null),
    pinned: !!carrierService,
  };
}

/** The same description as one sentence, for layouts with no room to mark it up. */
export function requestedShippingText(
  requested: OrderRequestedShipping | null | undefined,
): string {
  const { value, detail, pinned } = describeRequestedShipping(requested);

  if (!value) return 'Requested method not provided by marketplace';

  return [
    `Requested “${value}”`,
    detail ? ` (${detail})` : '',
    pinned ? ' — carrier pinned by marketplace' : '',
  ].join('');
}

export type DefaultShippingSpeedInput = {
  /** The tier the marketplace dictated, if any. */
  requestedSpeed: string | null | undefined;
  /** The speeds the warehouse being selected ships at. */
  warehouseSpeeds: string[] | null | undefined;
  /** What the form holds right now. */
  currentSpeed: string | null | undefined;
  /**
   * The marketplace tier this form last defaulted to. A `currentSpeed` that
   * differs from it can only have been typed in by the operator, which is what
   * separates an intentional override from a leftover warehouse default.
   */
  appliedRequestedSpeed: string | null | undefined;
};

export type DefaultShippingSpeed = {
  speed: string;
  /** Whether `speed` is the marketplace's instruction rather than a fallback. */
  fromMarketplace: boolean;
};

/**
 * Picks the speed to put in the form when the warehouse changes. The marketplace
 * instruction outranks the warehouse's own first-listed speed, which is what used
 * to win and is how orders shipped below the service level the buyer paid for.
 * An operator who picks a different speed still keeps it.
 */
export function resolveDefaultShippingSpeed({
  requestedSpeed,
  warehouseSpeeds,
  currentSpeed,
  appliedRequestedSpeed,
}: DefaultShippingSpeedInput): DefaultShippingSpeed {
  const shipsAt = (speed: string | null | undefined) =>
    !!speed &&
    Array.isArray(warehouseSpeeds) &&
    warehouseSpeeds.includes(speed);

  const overridden =
    !!currentSpeed &&
    !!appliedRequestedSpeed &&
    currentSpeed !== appliedRequestedSpeed;

  if (!overridden && shipsAt(requestedSpeed)) {
    return { speed: requestedSpeed as string, fromMarketplace: true };
  }

  if (shipsAt(currentSpeed)) {
    return { speed: currentSpeed as string, fromMarketplace: false };
  }

  return { speed: warehouseSpeeds?.[0] || '', fromMarketplace: false };
}
